import { useEffect, useState } from 'react';
import Tooltip from '@components/common/Tooltip';
import { api } from '@/api/growth/dashboard/api';
import {
    FarmlandRecommendationListItem,
    GrowthDashboardReq,
} from '@/api/growth/dashboard/type';

interface GrowthDashboardFarmingRecomCardProps {
    gfIdx: number;
}

const GrowthDashboardFarmingRecomCard = ({
    gfIdx,
}: GrowthDashboardFarmingRecomCardProps) => {
    const [loading, setLoading] = useState(true);
    const [recomList, setRecomList] = useState<
        FarmlandRecommendationListItem[]
    >([]);

    useEffect(() => {
        fetchRecomList();
    }, [gfIdx]);

    const fetchRecomList = async () => {
        setLoading(true);
        try {
            const req: GrowthDashboardReq = { gfIdx };
            const response = await api.getFarmingRecomList(req);
            setRecomList(response.data || []);
        } catch (error) {
            console.error('농작업추천 조회 실패:', error);
            setRecomList([]);
        } finally {
            setLoading(false);
        }
    };

    // 메시지 유형별 색상 및 라벨 매핑
    const getMessageTypeStyle = (messageType: string) => {
        const styleMap: {
            [key: string]: { bg: string; color: string; label: string };
        } = {
            농약: {
                bg: 'bg-secondary',
                color: 'bg-secondary',
                label: '병충해',
            },
            비료: { bg: 'bg-success', color: 'bg-success', label: '비료' },
            관수: { bg: 'bg-info', color: 'bg-info', label: '관수' },
            퇴비: { bg: 'bg-primary', color: 'bg-primary', label: '잔재물' },
            경운: { bg: 'bg-pink', color: 'bg-pink', label: '경운' },
        };
        return (
            styleMap[messageType] || {
                bg: 'bg-light',
                color: 'bg-secondary',
                label: messageType,
            }
        );
    };

    // 날짜 포맷팅 (2025-04-27 -> 2025년 4월 27일)
    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
    };

    // 요일 구하기
    const getDayOfWeek = (dateStr: string) => {
        const days = ['일', '월', '화', '수', '목', '금', '토'];
        const date = new Date(dateStr);
        return days[date.getDay()];
    };

    return (
        <div className="card custom-card overflow-hidden h-100 d-flex flex-column">
            <div className="card-header flex-shrink-0">
                <div className="card-title">
                    농작업의사결정
                    <Tooltip
                        content="AI 기반 농작업 추천 목록입니다"
                        customClass="tooltip-dark"
                        placement="top"
                    >
                        <i className="ri-question-line" />
                    </Tooltip>
                </div>
            </div>
            <div
                className="card-body pt-4"
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                    maxHeight: 3830,
                }}
            >
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">로딩중...</span>
                        </div>
                    </div>
                ) : recomList.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        추천 농작업 내역이 없습니다.
                    </div>
                ) : (
                    <div className="timeline container">
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="timeline-container">
                                    <div className="timeline-continue pt-0">
                                        {recomList.map((item, index) => {
                                            const style = getMessageTypeStyle(
                                                item.messageType,
                                            );
                                            return (
                                                <div
                                                    key={`${item.recIdx}-${index}`}
                                                    className="timeline-right"
                                                >
                                                    <div className="timeline-content">
                                                        <div
                                                            className={`d-flex gap-2 justify-content-between p-2 bg-light mb-3 flex-wrap rounded align-items-start ${style.bg}-transparent`}
                                                        >
                                                            <div className="timeline-end">
                                                                <span
                                                                    className={`p-1 fs-11 ${style.color} text-fixed-white text-center rounded-1 lh-1 fw-medium`}
                                                                >
                                                                    {formatDate(
                                                                        item.recDate,
                                                                    )}
                                                                    {item.isUrgent ===
                                                                        1 && (
                                                                        <span className="ms-1">
                                                                            🚨
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                            <p className="timeline-date text-muted mb-0">
                                                                {getDayOfWeek(
                                                                    item.recDate,
                                                                )}
                                                                요일
                                                            </p>
                                                        </div>
                                                        <div className="timeline-box">
                                                            <p className="text-muted mb-2">
                                                                <span className="text-default">
                                                                    <b>
                                                                        [
                                                                        {
                                                                            style.label
                                                                        }
                                                                        ]
                                                                        추천농작업
                                                                    </b>
                                                                    <br />
                                                                    <br />
                                                                    <span
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: item.recMessage.replace(
                                                                                /\n/g,
                                                                                '<br />',
                                                                            ),
                                                                        }}
                                                                    />
                                                                </span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GrowthDashboardFarmingRecomCard;
